/* eslint-disable react/no-unknown-property */
import { EnvironmentOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import _ from 'lodash';
import moment from 'moment';

import CheckNoIcon from '@/assets/icons/check_no.svg';
import CheckYesIcon from '@/assets/icons/check_yes.svg';

import FileRow from '@/components/Common/FileRow';
import TableColAttachment from '@/components/Common/TableColAttachment';
import UserAvatar from '@/components/Common/UserAvatar';
import ImagePreview from '@/components/FormBuilder/components/Table/components/ImagePreview';
import { CoreEnums } from '@/utils/domain/constants';
import {
  formatTZLocal,
  generatePublicFileURL,
  isJsonLike,
  isNilOrEmpty,
  isTrueBoolean,
  parseJson,
} from '@/utils/infrastructure';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

const { FormFieldStringType, FormFieldType, AttachmentFileTypeClassification } = CoreEnums;

const GroupFilter = {
  ...Filters.BaseFilters.RemoteFilter,
  name: 'dynamicEntityGroupId',
  label: 'global.text.group',
  // User-facing filter on `/entities` → uses the access-aware
  // `entity/groups` endpoint so non-admin roles only see the groups
  // they're allowed to filter by (matches the standard adopted by
  // `list/groups`, `report/groups`, `process/groups`). The admin
  // CRUD endpoint `dynamicEntityGroup/paged` is reserved for
  // `/configuration/dynamic-entity-groups`.
  apiRoute: 'entity/groups',
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.updatedDate',
  name: 'updatedDateRange',
};

export const filtersConfig = [
  GroupFilter,
  Filters.IsActiveFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters('entities', [
  GroupFilter,
  Filters.IsActiveFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
]);

export const handleTableColumnRender = (record, fld, entity = null) => {
  const data = record[fld.internalName];

  if (isNilOrEmpty(data)) return;

  switch (fld.type) {
    case FormFieldStringType.Boolean:
    case FormFieldType.Boolean:
      const icon = isTrueBoolean(data) ? CheckYesIcon : CheckNoIcon;
      return (
        <div align="center">
          <img alt="icon" className="anticon" src={icon} />
        </div>
      );

    case FormFieldStringType.Date:
    case FormFieldType.Date:
      return moment(data).format(fld.format ?? 'L');

    case FormFieldStringType.DateTime:
    case FormFieldType.DateTime:
      return formatTZLocal(data, fld.format);

    case FormFieldStringType.Image:
    case FormFieldStringType.Photo:
    case FormFieldStringType.Attachment:
    case FormFieldType.Image:
    case FormFieldType.Photo:
    case FormFieldType.Attachment: {
      const { attachments = [] } = parseJson(data);

      const item = _.head(attachments);
      const options = {
        entityId: entity?.id,
        fieldId: fld.id,
        dataId: record.id,
      };

      const { generatedPublicFileURL: previewUrl } = generatePublicFileURL(fld.type, options);
      const { generatedPublicFileURL: downloadUrl } = generatePublicFileURL(
        CoreEnums.FormFieldType.Attachment,
        options,
      );

      const attrs = item && {
        contentType: item.contentType || 'image/png',
        ...(item.content ? {} : { previewUrl }),
        downloadUrl: item.content ? _.split(item.content, 'base64,').pop() : downloadUrl,
        ..._.pick(item, ['fileName', 'fileTypeClassification', 'isBase64']),
      };

      if (fld.type === FormFieldType.Attachment || fld.type === FormFieldStringType.Attachment) {
        if (attachments.length > 1) {
          return (
            <Space size={24} wrap>
              {attachments.map((o) => {
                if (o.content || o.downloadUrl) return null;
                if (o.fileTypeClassification === AttachmentFileTypeClassification.Image) {
                  return <ImagePreview key={o.fileName} item={{ ...o, downloadUrl, previewUrl }} />;
                }

                return (
                  <FileRow
                    key={o.fileName}
                    {...o}
                    creator={null}
                    downloadUrl={downloadUrl}
                    previewUrl={previewUrl}
                  />
                );
              })}
            </Space>
          );
        }
        if (item && !item.contentType.includes('image')) {
          return <TableColAttachment item={{ ...item, ...attrs }} />;
        }
      }

      return attrs && <ImagePreview item={attrs} />;
    }

    case FormFieldStringType.SelectorPosition:
    case FormFieldStringType.SelectorUser:
    case FormFieldStringType.SelectorUserGroup:
    case FormFieldStringType.SelectorArea:
    case FormFieldStringType.WebServiceRemoteSearch:
    case FormFieldStringType.WebServiceRemoteList:
    case FormFieldStringType.DataBaseRemoteList:
    case FormFieldStringType.DataBaseRemoteSearch:
    case FormFieldStringType.FieldDynamicEntityList:
    case FormFieldStringType.FieldDynamicEntitySearch:
    case FormFieldStringType.Choice:

    case FormFieldType.SelectorPosition:
    case FormFieldType.SelectorUser:
    case FormFieldType.SelectorUserGroup:
    case FormFieldType.SelectorArea:
    case FormFieldType.WebServiceRemoteSearch:
    case FormFieldType.WebServiceRemoteList:
    case FormFieldType.DataBaseRemoteList:
    case FormFieldType.DataBaseRemoteSearch:
    case FormFieldType.FieldDynamicEntityList:
    case FormFieldType.FieldDynamicEntitySearch:
    case FormFieldType.Choice: {
      if (_.isString(data) && !isJsonLike(data)) {
        if (
          fld.type === FormFieldType.SelectorUser ||
          fld.type === FormFieldStringType.SelectorUser
        ) {
          return <UserAvatar name={data} />;
        }
        return data;
      }

      const parsed = parseJson(data) || {};
      const { text, label, items, displayText } = parsed;
      const iterate = (item) => item?.text ?? item?.label ?? item;

      if (items?.length > 1) return items.map(iterate).join('; ');
      if (Array.isArray(parsed)) return parsed.map(iterate).join('; ');
      if (
        fld.type === FormFieldType.SelectorUser ||
        fld.type === FormFieldStringType.SelectorUser
      ) {
        const item = items[0];
        return <UserAvatar name={item.text ?? item.label ?? displayText} />;
      }

      return text ?? label ?? displayText;
    }

    case FormFieldStringType.Url:
    case FormFieldType.Url: {
      return (
        <a href={data} target="_blank" rel="noreferrer">
          {data}
        </a>
      );
    }

    case FormFieldStringType.Location:
    case FormFieldType.Location: {
      let href = data;
      let title = data;

      if (isJsonLike(data)) {
        const { name, lat, lng } = parseJson(data);
        href = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
        title = name;
      }

      return (
        <a href={href} target="_blank" rel="noreferrer">
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {title}
        </a>
      );
    }

    case FormFieldStringType.Password:
    case FormFieldType.Password: {
      return ''.padStart(data.length, '*');
    }

    default:
      return data;
  }
};

export const isAttachment = (type) => {
  return (
    type === FormFieldType.Attachment ||
    type === FormFieldType.Photo ||
    type === FormFieldType.Image ||
    type === FormFieldType.Signature ||
    type === FormFieldStringType.Attachment ||
    type === FormFieldStringType.Photo ||
    type === FormFieldStringType.Image ||
    type === FormFieldStringType.Signature
  );
};

export const isBoolean = (type) => {
  return type === FormFieldType.Boolean || type === FormFieldStringType.Boolean;
};

export const isJSONData = (type) => {
  return (
    type === FormFieldType.SelectorUser ||
    type === FormFieldType.SelectorPosition ||
    type === FormFieldType.SelectorUserGroup ||
    type === FormFieldType.SelectorArea ||
    type === FormFieldType.WebServiceRemoteSearch ||
    type === FormFieldType.WebServiceRemoteList ||
    type === FormFieldType.DataBaseRemoteList ||
    type === FormFieldType.DataBaseRemoteSearch ||
    type === FormFieldStringType.SelectorUser ||
    type === FormFieldStringType.SelectorPosition ||
    type === FormFieldStringType.SelectorUserGroup ||
    type === FormFieldStringType.SelectorArea ||
    type === FormFieldStringType.WebServiceRemoteSearch ||
    type === FormFieldStringType.WebServiceRemoteList ||
    type === FormFieldStringType.DataBaseRemoteList ||
    type === FormFieldStringType.DataBaseRemoteSearch
  );
};

export const generateAttachmentURLs = (type, options) => {
  const { generatedPublicFileURL } = generatePublicFileURL(type, options);
  const { generatedPublicFileURL: generateDownloadFileURL } = generatePublicFileURL(
    FormFieldType.Attachment,
    options,
  );

  return {
    generatedPublicFileURL,
    generateDownloadFileURL,
  };
};

export const mapAttachmentPublicURLs = (value, fld, options) => {
  const { attachments = [], ...more } = value || {};

  const files = attachments.map((att) => {
    if (att?.id) {
      const { generatedPublicFileURL, generateDownloadFileURL } = generateAttachmentURLs(fld.type, {
        fieldId: fld.id,
        ...options,
        ...(fld.multiple && { fileId: att.id, fileName: att.fileName }),
      });

      return {
        ...att,
        previewUrl: generatedPublicFileURL,
        downloadUrl: generateDownloadFileURL,
      };
    }

    return att;
  });

  return { ...more, attachments: files };
};
